import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import emailjs from "emailjs-com";
import { cartUtils } from "../utils/userCartWishlistUtils";
import "./Checkout.css";

const Checkout = ({ isSticky }) => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [landmark, setLandmark] = useState("");
  const [address, setAddress] = useState("");
  const [mobile2, setMobile2] = useState("");
  const [pincode, setPincode] = useState("");
  const [deliveryCharge, setDeliveryCharge] = useState(0);
  const [totalPay, setTotalPay] = useState(0);
  const [showAlert, setShowAlert] = useState(false);
  const [productSizes, setProductSizes] = useState({});
  const [productPrices, setProductPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [cartItems, setCartItems] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [showAlertRequiredFields, setshowAlertRequiredFields] = useState(false);

  const location = useLocation();
  const userData = JSON.parse(sessionStorage.getItem("user"));

  // Load cart items and product details
  useEffect(() => {
    const loadCartItems = async () => {
      try {
        const cart = cartUtils.getCart();
        const productIds = Object.keys(cart);

        // Fetch all products
        const productRes = await axios.get(
          "https://api.dremerz.net/api/DZ_8925_KalanjiyamEcom_Products_AllProducts_ProductsList"
        );
        const allProducts = productRes.data;

        const items = productIds
          .map((id) => {
            const product = allProducts.find((p) => p.id.toString() === id);
            if (!product) return null;

            // Parse variants JSON string
            let parsedVariants = [];
            try {
              parsedVariants = JSON.parse(product.variants || "[]");
            } catch (err) {
              console.warn(
                "Invalid variant format for product:",
                product.productName
              );
            }

            const availableSizes = parsedVariants.map(
              (variant) => variant.variantUnit
            );
            const availableQuantities = {};
            const variantPrices = {};

            parsedVariants.forEach((variant) => {
              availableQuantities[variant.variantUnit] = parseInt(
                variant.stockQuantity || "0"
              );
              variantPrices[variant.variantUnit] = parseFloat(
                variant.price || product.sellingPrice
              );
            });

            return {
              id,
              quantity: cart[id],
              ...product,
              availableSizes,
              availableQuantities,
              variantPrices,
              parsedVariants,
            };
          })
          .filter((item) => item);

        setCartItems(items);

        // Calculate initial grand total based on default prices
        const total = items.reduce(
          (acc, item) =>
            acc +
            parseFloat(item.quantity) * parseFloat(item.sellingPrice || 0),
          0
        );
        setGrandTotal(total);

        setLoading(false);
      } catch (error) {
        console.error("Error loading cart items:", error);
        setLoading(false);
      }
    };

    loadCartItems();
  }, []);

  // Update grand total when product sizes/prices change
  useEffect(() => {
    const total = cartItems.reduce((acc, item) => {
      const selectedSize = productSizes[item.id];
      const itemPrice = selectedSize
        ? item.variantPrices[selectedSize] || item.sellingPrice
        : item.sellingPrice;

      return acc + parseFloat(item.quantity) * parseFloat(itemPrice);
    }, 0);

    setGrandTotal(total);
  }, [cartItems, productSizes, productPrices]);

  // Calculate delivery charge based on total items
  useEffect(() => {
    const totalItems = cartItems.reduce(
      (sum, item) => sum + Number(item.quantity),
      0
    );

    if (totalItems <= 2) {
      setDeliveryCharge(80);
    } else if (totalItems <= 4) {
      setDeliveryCharge(120);
    } else {
      setDeliveryCharge(180);
    }
  }, [cartItems]);

  // Update total payable amount
  useEffect(() => {
    setTotalPay(grandTotal + deliveryCharge);
  }, [grandTotal, deliveryCharge]);

  // Check the window size on mount and resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 576);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isFormValid = () => {
    // Check if all required fields are filled and all products have sizes selected
    const allSizesSelected = cartItems.every((item) => productSizes[item.id]);
    return (
      address !== "" &&
      landmark !== "" &&
      pincode !== "" &&
      deliveryCharge > 0 &&
      allSizesSelected
    );
  };

  const handlePaymentCheck = async () => {
    try {
      if (isFormValid()) {
        handlePayment();
      } else {
        setshowAlertRequiredFields(true);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const sendEmail = async (paymentData) => {
    const emailParams = {
      fullname: paymentData.get("user_fullname"),
      email: paymentData.get("user_email"),
      mobile: paymentData.get("user_mobile"),
      product: paymentData.get("product_name"),
      price: paymentData.get("paymentTotal"),
      bookingid: paymentData.get("bookingid"),
      razorPayID: paymentData.get("razorPayID"),
    };

    try {
      await emailjs.send(
        "service_kabqwp7",
        "template_9vlzohn",
        emailParams,
        "aJnP5PotDQdflEJWg"
      );
      console.log("Order confirmation email sent");
    } catch (error) {
      console.error("Error sending email:", error);
    }
  };

  const getCurrentDateTime = () => {
    const now = new Date();
    const date = String(now.getDate()).padStart(2, "0");
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");

    return `${date}+${month}+${year}_${hours}${minutes}${seconds}`;
  };

  // Handle size change - update both size and price
  const handleSizeChange = (productId, size) => {
    setProductSizes((prev) => ({
      ...prev,
      [productId]: size,
    }));

    // Find the product to get its variant price
    const product = cartItems.find((item) => item.id === productId);
    if (product && product.variantPrices && product.variantPrices[size]) {
      setProductPrices((prev) => ({
        ...prev,
        [productId]: product.variantPrices[size],
      }));
    }
  };

  // Update quantity in SQL DB (keep this function as is)
  const updateQuantityInSQLDB = async (productCode, quantity) => {
    if (!productCode) return;

    try {
      const response = await axios.get(
        `https://kalanjiyam-b1.idlbay.in/index.php/api/update_quantity_in_db/${productCode}/${quantity}`
      );
      console.log("SQL DB Quantity updated:", response.data);
    } catch (error) {
      console.error("Error updating SQL DB quantity:", error);
    }
  };

  // Updated product quantity update function
  const updateProductQuantityInAPI = async (productId, size, newQuantity) => {
    try {
      // Step 1: Fetch the existing product
      const response = await axios.get(
        `https://api.dremerz.net/api/DZ_8925_KalanjiyamEcom_Products_AllProducts_ProductsList/${productId}`
      );

      const product = response.data;

      if (!product || !product.variants) {
        console.error("Product or variants not found.");
        return;
      }

      // Step 2: Parse variants JSON
      let variants = [];
      try {
        variants = JSON.parse(product.variants);
      } catch (err) {
        console.error("Failed to parse variants JSON:", err);
        return;
      }

      // Step 3: Update the stock quantity for the specified size
      const variantIndex = variants.findIndex(
        (variant) => variant.variantUnit === size
      );

      if (variantIndex === -1) {
        console.error(
          `Variant size '${size}' not found in product ${productId}`
        );
        return;
      }

      // Ensure we don't go below 0
      const updatedQuantity = Math.max(0, newQuantity);
      variants[variantIndex].stockQuantity = updatedQuantity.toString();

      // Step 4: Update the product with modified variants
      const updatedProduct = {
        ...product,
        variants: JSON.stringify(variants),
      };

      // Step 5: Send PUT request to update
      await axios.put(
        `https://api.dremerz.net/api/DZ_8925_KalanjiyamEcom_Products_AllProducts_ProductsList/${productId}`,
        updatedProduct
      );

      console.log(
        `✅ Quantity updated for product ${productId}, size ${size} to ${updatedQuantity}`
      );

      // Update local state to reflect the change
      setCartItems((prev) =>
        prev.map((item) => {
          if (item.id === productId) {
            return {
              ...item,
              availableQuantities: {
                ...item.availableQuantities,
                [size]: updatedQuantity,
              },
            };
          }
          return item;
        })
      );
    } catch (error) {
      console.error("❌ Error updating product quantity:", error);
      throw error;
    }
  };

  const handleProceedToPay = async (razorpayPaymentId) => {
    if (!isFormValid()) {
      setshowAlertRequiredFields(true);
      return;
    }

    try {
      // Process each product in the cart
      await Promise.all(
        cartItems.map(async (item) => {
          const selectedSize = productSizes[item.id];
          if (!selectedSize) {
            throw new Error(`Please select a size for ${item.brandName}`);
          }

          // Check stock availability
          const currentQuantity = parseInt(
            item.availableQuantities[selectedSize] || 0
          );
          const orderedQuantity = parseInt(item.quantity);

          if (currentQuantity < orderedQuantity) {
            throw new Error(
              `Not enough stock for size ${selectedSize} in ${item.brandName}`
            );
          }

          // Get the correct price for the selected variant
          const itemPrice =
            item.variantPrices?.[selectedSize] || item.sellingPrice;

          // Prepare order data
          const formData = new FormData();
          formData.append("user_id", userData.id);
          formData.append("user_fullname", userData.fullname);
          formData.append("user_email", userData.email);
          formData.append("user_mobile", userData.mobile);
          formData.append("product_id", item.id);
          formData.append("product_name", item.productName);
          formData.append("product_brand", item.brandName);
          formData.append("product_price", itemPrice);
          formData.append("product_quantity", item.quantity);
          formData.append("product_grand_total", totalPay);
          formData.append("delivery_charge", deliveryCharge);
          formData.append("product_originalPrice", item.originalPrice || "");
          formData.append("product_image", item.productImage);
          formData.append("address", address);
          formData.append("landmark", landmark);
          formData.append("mobile2", mobile2);
          formData.append("pincode", pincode);
          formData.append("paymentTotal", totalPay);
          formData.append("razorPayID", razorpayPaymentId);
          formData.append("status", "Order Placed");
          formData.append("date_time", new Date().toLocaleString());
          formData.append("dress_size", selectedSize);
          formData.append("productCode", item.productCode || "");

          // Generate booking ID
          const secretId = `SECkalboq+${getCurrentDateTime()}+${String(
            new Date().getSeconds()
          ).padStart(2, "0")}`;

          const currentDate = new Date();
          const day = String(currentDate.getDate()).padStart(2, "0");
          const currentMonth = String(currentDate.getMonth() + 1).padStart(
            2,
            "0"
          );
          const currentYear = currentDate.getFullYear();

          const newBookingId = `BIkalboq${day}${currentMonth}${currentYear}${String(
            Math.floor(Math.random() * 100)
          ).padStart(2, "0")}`;

          formData.append("bookingid", newBookingId);
          formData.append("secretId", secretId);

          // 1. Place the order
          await axios.post(
            "https://api.dremerz.net/api/Kalanjiyam_User_Order_Product_Checkout",
            formData
          );

          // 2. Update quantities
          const newQuantity = currentQuantity - orderedQuantity;

          // Update in SQL DB if productCode exists
          if (item.productCode) {
            await updateQuantityInSQLDB(item.productCode, orderedQuantity);
          }

          // Update in the product API
          await updateProductQuantityInAPI(item.id, selectedSize, newQuantity);

          // 3. Send confirmation email
          await sendEmail(formData);
        })
      );

      // Clear the cart after successful order - using the utility function
      await cartUtils.clearCart();

      // Update local state
      setCartItems([]);
      setProductSizes({});
      setProductPrices({});

      // Redirect to orders page
      navigate("/TrackYourOrder/myOrders");
      setShowAlert(true);
    } catch (error) {
      console.error("Error processing order:", error);
      alert(error.message || "Error processing your order. Please try again.");
    }
  };

  const handlePayment = () => {
    const loadScript = (src) => {
      return new Promise((resolve) => {
        const script = document.createElement("script");
        script.src = src;
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
      });
    };

    loadScript("https://checkout.razorpay.com/v1/checkout.js").then(
      (loaded) => {
        if (!loaded) {
          alert("Failed to load Razorpay SDK. Please check your connection.");
          return;
        }

        const options = {
          key: "rzp_test_FNu62GtN5qZaVx",
          key_secret: "PRIExQVH2t9DAuFTyS4MIwF7",
          amount: Math.ceil(totalPay) * 100, // Amount in paise
          currency: "INR",
          name: "Kalanjiyam",
          description: "Order Payment",
          handler: async (response) => {
            await handleProceedToPay(response.razorpay_payment_id);
          },
          prefill: {
            name: userData.fullname,
            email: userData.email,
            contact: userData.mobile,
          },
          theme: {
            color: "#920504",
          },
        };

        const pay = new window.Razorpay(options);
        pay.open();
      }
    );
  };

  const updateCartQuantity = async (productId, newQuantity) => {
    if (newQuantity < 1) {
      // Remove from cart if quantity is 0
      await cartUtils.removeFromCart(productId);
      setCartItems((prev) => prev.filter((item) => item.id !== productId));
      return;
    }

    try {
      await cartUtils.updateQuantity(productId, newQuantity);
      setCartItems((prev) =>
        prev.map((item) =>
          item.id === productId ? { ...item, quantity: newQuantity } : item
        )
      );

      // Recalculate grand total
      const selectedSize = productSizes[productId];
      const product = cartItems.find((item) => item.id === productId);
      const itemPrice = selectedSize
        ? product?.variantPrices?.[selectedSize] || product?.sellingPrice
        : product?.sellingPrice;

      const total = cartItems.reduce(
        (acc, item) =>
          acc +
          parseFloat(item.id === productId ? newQuantity : item.quantity) *
            parseFloat(
              item.id === productId
                ? itemPrice
                : productSizes[item.id]
                ? item.variantPrices?.[productSizes[item.id]] ||
                  item.sellingPrice
                : item.sellingPrice
            ),
        0
      );
      setGrandTotal(total);
    } catch (error) {
      console.error("Error updating cart quantity:", error);
    }
  };

  if (loading) {
    return (
      <div className="checkout-container d-flex justify-content-center align-items-center container my-5">
        <div className="loading-spinner">Loading your order...</div>
      </div>
    );
  }

  return (
    <div id="adres" className="checkout-page container my-5">
      {/* Products Grid */}
      <div className="checkout-products-grid row mb-4">
        <h4 className="checkout-section-title mb-3">Your Items</h4>
        {cartItems.map((item) => {
          const selectedSize = productSizes[item.id];
          const itemPrice = selectedSize
            ? item.variantPrices?.[selectedSize] || item.sellingPrice
            : item.sellingPrice;

          return (
            <div
              className="checkout-product-col col-lg-6 col-md-6 col-12 mb-4"
              key={item.id}
            >
              <div className="checkout-product-card p-3 h-100">
                <div className="row align-items-center">
                  <div className="checkout-product-img-col col-4">
                    <img
                      src={`https://api.dremerz.net/uploads/${item.productImage}`}
                      alt={item.brandName}
                      className="checkout-product-img img-fluid rounded"
                    />
                  </div>
                  <div className="checkout-product-details-col col-8">
                    <h6 className="checkout-product-title mb-1">
                      {item.productName}
                    </h6>
                    <p className="checkout-product-price text-danger mb-1">
                      ₹{(item.quantity * itemPrice).toFixed(2)}
                    </p>

                    <div className="checkout-quantity-controls d-flex align-items-center mb-2">
                      <button
                        className="checkout-quantity-btn btn btn-outline-dark btn-sm"
                        onClick={() =>
                          updateCartQuantity(item.id, item.quantity - 1)
                        }
                        disabled={item.quantity <= 1}
                      >
                        -
                      </button>
                      <input
                        type="text"
                        className="checkout-quantity-input form-control text-center mx-2"
                        value={item.quantity}
                        readOnly
                      />
                      <button
                        className="checkout-quantity-btn btn btn-outline-dark btn-sm"
                        onClick={() =>
                          updateCartQuantity(item.id, item.quantity + 1)
                        }
                        disabled={
                          selectedSize &&
                          item.availableQuantities[selectedSize] <=
                            item.quantity
                        }
                      >
                        +
                      </button>
                    </div>
                    <select
                      className="checkout-size-select form-select form-select-sm mb-2"
                      value={selectedSize || ""}
                      onChange={(e) =>
                        handleSizeChange(item.id, e.target.value)
                      }
                      required
                    >
                      <option value="" style={{ maxWidth: "100px" }}>
                        Select size
                      </option>
                      {item.availableSizes?.map((size) => (
                        <option
                          key={size}
                          value={size}
                          disabled={item.availableQuantities[size] <= 0}
                          className="checkout-size-option"
                          style={{ maxWidth: "100px" }}
                        >
                          {size} - ₹
                          {item.variantPrices?.[size] || item.sellingPrice}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Order Summary Section */}
      <div className="checkout-order-summary p-4 mb-4">
        <h4 className="checkout-section-title mb-3">Order Summary</h4>
        <table className="checkout-summary-table table table-bordered">
          <tbody>
            <tr>
              <td>Delivery Charge</td>
              <td className="text-end">₹{deliveryCharge}</td>
            </tr>
            <tr>
              <td>Subtotal</td>
              <td className="text-end">₹{grandTotal.toFixed(2)}</td>
            </tr>
            <tr className="checkout-total-row fw-bold">
              <td>Total Payable</td>
              <td className="text-end">
                ₹{(grandTotal + deliveryCharge).toFixed(2)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Address Section */}
      <div className="checkout-address-section p-4 mb-4">
        <h4 className="checkout-section-title mb-3">Delivery Address</h4>
        <div className="row">
          <div className="col-md-12 mb-3">
            <label htmlFor="address" className="checkout-form-label form-label">
              Full Address (Required)
            </label>
            <input
              type="text"
              className="checkout-form-input form-control"
              id="address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              required
            />
          </div>
          <div className="col-md-6 mb-3">
            <label
              htmlFor="landmark"
              className="checkout-form-label form-label"
            >
              Landmark (Required)
            </label>
            <input
              type="text"
              className="checkout-form-input form-control"
              id="landmark"
              value={landmark}
              onChange={(e) => setLandmark(e.target.value)}
              required
            />
          </div>
          <div className="col-md-6 mb-3">
            <label htmlFor="mobile2" className="checkout-form-label form-label">
              Mobile Number (Required)
            </label>
            <input
              type="text"
              className="checkout-form-input form-control"
              id="mobile2"
              value={mobile2}
              onChange={(e) => setMobile2(e.target.value)}
              required
            />
          </div>
          <div className="col-md-6 mb-3">
            <label htmlFor="pincode" className="checkout-form-label form-label">
              Pincode (Required)
            </label>
            <input
              type="number"
              className="checkout-form-input form-control"
              id="pincode"
              value={pincode}
              onChange={(e) => setPincode(e.target.value.slice(0, 6))}
              required
            />
          </div>
        </div>
      </div>

      {/* Proceed to Pay Button */}
      <div className="checkout-payment-actions text-center">
        <button
          className="checkout-pay-btn btn btn-danger py-2 px-5"
          onClick={handlePaymentCheck}
        >
          Proceed to Pay
        </button>
      </div>

      {/* Order Confirmation Modal */}
      {showAlert && (
        <div
          className="modal show"
          style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" style={{ color: "#920504" }}>
                  Order Confirmation
                </h5>
              </div>
              <div className="modal-body">
                <p style={{ color: "#920504" }}>
                  Your order has been placed successfully!
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setShowAlert(false);
                    navigate("/TrackYourOrder/myOrders");
                  }}
                >
                  View Orders
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAlertRequiredFields && (
        <div
          className="modal show"
          style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" style={{ color: "#920504" }}>
                  Info
                </h5>
              </div>
              <div className="modal-body">
                <p style={{ color: "#920504" }}>
                  Please Fill all the required fields before proceeding to pay
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  style={{ color: "white", background: "#920504" }}
                  className="btn"
                  onClick={() => {
                    setshowAlertRequiredFields(false);
                    document
                      .getElementById("adres")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Checkout;
